import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useTheme, useMediaQuery, Stack, Box } from "@mui/system";
import {
    Domain,
    Link as LinkIcon,
    Flag,
    Category,
    Groups,
    Public,
    TrackChanges,
    SportsScore,
} from "@mui/icons-material";
import StepperSidebar from "./StepperSidebar";
import StepperMobileNav from "./StepperMobileNav";
import StepperContent from "./StepperContent";

import OrganizationProfileInfoPage from "views/account/organization-profile/OrganizationProfileInfo.Page";
import BusinessNumberStep from "./steps/BusinessNumberStep";
import LinksStep from "./steps/LinksStep";
import HeaderAndFooterStep from "./steps/HeaderAndFooterStep";
import MissionStatementStep from "./steps/MissionStatementStep";
import CategoriesStep from "./steps/CategoriesStep";
import PopulationsServedStep from "./steps/PopulationsServedStep";
import GeographicAreasServedStep from "./steps/GeographicAreasServedStep";
import UNSDGGoalsAlignmentStep from "./steps/UNSDGGoalsAlignmentStep";
import LastStep from "./steps/LastStep";

import useStepCompleteToggle from "./useStepCompleteToggle";
import { selectCompletedStepIdList } from "./slice";
import { showErrorToaster } from "store/Shared.Slice";
import { selectOrganizationInfoHasError } from "views/account/organization-profile/OrganizationProfileInfo.Slice";

export const steps = [
    {
        id: "organizationInfo",
        title: "Organization Information",
        description: "Company name, address & contact info",
        icon: Domain,
    },
    {
        id: "links",
        title: "Links",
        description: "Website and social media links",
        icon: LinkIcon,
    },
    {
        id: "missionStatement",
        title: "Mission Statement",
        description: "Add organization mission statement",
        icon: Flag,
    },
    {
        id: "categories",
        title: "Sectors",
        description: "Select sectors",
        icon: Category,
    },
    {
        id: "populationsServed",
        title: "Populations Served",
        description: "Add population served",
        icon: Groups,
    },
    {
        id: "gas",
        title: "Geographic Areas Served",
        description: "Add geographic areas served",
        icon: Public,
    },
    {
        id: "sdg",
        title: "UN SDG Goals Alignment",
        description: "Add UN SDG goals alignment",
        icon: TrackChanges,
    },
    {
        id: "lastStep",
        title: "Last Step",
        icon: SportsScore,
    },
];

const OrganizationProfileStepper = ({
    onStep,
    complete = true,
    onComplete,
    popupMode = false,
    actionsDisabled = false
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matchUpMd = useMediaQuery((theme) => theme.breakpoints.up("lg"));
    const updateCompletedSteps = useStepCompleteToggle();

    const [activeStepId, setActiveStepId] = useState("organizationInfo");
    const completedStepIdList = useSelector(selectCompletedStepIdList);
    const organizationInfoHasError = useSelector(selectOrganizationInfoHasError);
    const errorStepIdList = organizationInfoHasError ? ["organizationInfo"] : [];
    const saving = useSelector(
        (state) => state.organizationStepperState.saving
    );

    const processedSteps = complete ? steps : steps.slice(0, -1);
    const activeStep = processedSteps.find((step) => step.id === activeStepId);
    const activeStepIndex = processedSteps.indexOf(activeStep);
    const completedPercentage = Math.round(
        (completedStepIdList.length /
            (processedSteps.length - (complete ? 1 : 0))) *
            100
    );

    const handleClickStep = (step) => {
        if (
            activeStepId === "organizationInfo" &&
            step.id !== "organizationInfo" &&
            !completedStepIdList.includes("organizationInfo")
        ) {
            dispatch(showErrorToaster(t("errors.organization_info_incomplete")));
            return;
        }

        setActiveStepId(step.id);
        onStep?.({ nextStepId: step.id });
    };

    const setNextStep = (nextStepIndex) => {
        const nextStepId = processedSteps[nextStepIndex].id;
        setActiveStepId(nextStepId);
        onStep?.({ nextStepId });
    };

    const handleBack = () => {
        const nextStepIndex = activeStepIndex - 1;
        setNextStep(nextStepIndex);
    };

    const handleNext = () => {
        const nextStepIndex = activeStepIndex + 1;
        setNextStep(nextStepIndex);
    };

    const renderActiveStep = () => {
        switch (activeStepId) {
            case "organizationInfo":
                return <OrganizationProfileInfoPage />;
            case "bnein":
                return <BusinessNumberStep />;
            case "links":
                return <LinksStep />;
            case "headerFooter":
                return <HeaderAndFooterStep />;
            case "missionStatement":
                return <MissionStatementStep />;
            case "categories":
                return <CategoriesStep />;
            case "populationsServed":
                return <PopulationsServedStep />;
            case "gas":
                return <GeographicAreasServedStep />;
            case "sdg":
                return <UNSDGGoalsAlignmentStep />;
            case "lastStep":
                return <LastStep />;
            default:
                <></>;
        }
    };

    useEffect(() => {
        updateCompletedSteps();
    }, [updateCompletedSteps]);

    return (
        <Stack direction={matchUpMd ? "row" : "column"} height={matchUpMd ? 690 : "auto"}>
            {matchUpMd ? (
                <Box
                    flexShrink={0}
                    flexBasis={400}
                    bgcolor="grey.100"
                    p={2}
                    py={4}
                    sx={{
                        bgcolor: "grey.100",
                        ...theme.applyStyles("dark", {
                            bgcolor: "background.paper2",
                        }),
                    }}
                >
                    <StepperSidebar
                        activeStepId={activeStepId}
                        steps={processedSteps}
                        completedPercentage={completedPercentage}
                        completedIdList={completedStepIdList}
                        errorIdList={errorStepIdList}
                        onClickStep={handleClickStep}
                    />
                </Box>
            ) : (
                <Box
                    px={2}
                    pt={2}
                    pb={1}
                    sx={{
                        bgcolor: "grey.100",
                        ...theme.applyStyles("dark", {
                            bgcolor: "background.paper2",
                        }),
                    }}
                >
                    <StepperMobileNav
                        activeStepId={activeStepId}
                        steps={processedSteps}
                        completedIdList={completedStepIdList}
                        errorIdList={errorStepIdList}
                        onClickStep={handleClickStep}
                    />
                </Box>
            )}
            <Box flex="1 0" height={matchUpMd ? "100%" : "auto"} overflow="auto" pb={2}>
                <StepperContent
                    title={activeStep.title}
                    activeStepNumber={activeStepIndex + 1}
                    stepsCount={processedSteps.length}
                    backDisabled={activeStepIndex === 0}
                    nextDisabled={activeStepIndex === processedSteps.length - 1}
                    onNext={handleNext}
                    onBack={handleBack}
                    onComplete={onComplete}
                    saving={saving}
                    popupMode={popupMode}
                    disabled={actionsDisabled}
                >
                    {renderActiveStep()}
                </StepperContent>
            </Box>
        </Stack>
    );
};

export default OrganizationProfileStepper;
